import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '~/lib/cn';

/**
 * The width of a keypad, exported so callers can line other controls up with it.
 *
 * A submit button under the pad has to stop at the same edge — stretched across the whole
 * dialog it stops reading as "the button belonging to these keys" and starts reading as a
 * page action.
 */
export const KEYPAD_WIDTH = 'tw-w-72';

interface KeypadProps {
    /** The digits entered so far. The caller owns them. */
    value: string;
    onChange: (value: string) => void;
    /** Hard cap on the number of digits; further presses are ignored. */
    maxLength?: number;
    /** Fired when the cap is reached, so a caller can submit without a second tap. */
    onComplete?: (value: string) => void;
    /** Accessible name for the backspace key — the icon alone announces nothing. */
    backspaceLabel: string;
    /** Accessible name for the clear key, which reads only as "C". */
    clearLabel: string;
    className?: string;
}

/** One key, sized for a finger on a wall-mounted screen rather than a cursor. */
const keyClasses =
    'tw-flex tw-h-16 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-accents-1 tw-text-2xl tw-font-bold tw-text-accents-9 tw-transition-colors hover:tw-bg-accents-2';

/**
 * An on-screen number pad.
 *
 * Laid out like a phone — 1-9, then clear, zero, backspace — because that is the order
 * every hand already knows. The till and the attendance check-in both used to carry their
 * own copy, and they disagreed: one ran 0-9 top to bottom across full-width bars, the
 * other 1-9 across fixed keys, so the same club member met two different pads on two
 * screens in the same room.
 *
 * Stateless on purpose. The value lives with the caller, which is what lets one screen
 * keep it in component state and another hand it to react-hook-form.
 */
export default function Keypad({
    value,
    onChange,
    maxLength,
    onComplete,
    backspaceLabel,
    clearLabel,
    className
}: KeypadProps) {
    /** Appends a digit, stopping at the cap and reporting when it is reached. */
    const press = (digit: string) => {
        if (undefined !== maxLength && value.length >= maxLength) return;

        const next = value + digit;
        onChange(next);

        if (undefined !== maxLength && next.length === maxLength) onComplete?.(next);
    };

    return (
        <div className={cn('tw-grid tw-grid-cols-3 tw-gap-2', KEYPAD_WIDTH, className)}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                <button key={digit} type="button" className={keyClasses} onClick={() => press(digit)}>
                    {digit}
                </button>
            ))}

            <button type="button" aria-label={clearLabel} className={keyClasses} onClick={() => onChange('')}>
                C
            </button>
            <button type="button" className={keyClasses} onClick={() => press('0')}>
                0
            </button>
            <button
                type="button"
                aria-label={backspaceLabel}
                className={keyClasses}
                onClick={() => onChange(value.slice(0, -1))}
            >
                <FontAwesomeIcon icon={faDeleteLeft} />
            </button>
        </div>
    );
}
